import { useState } from 'react';

import { ConfidenceWebsiteLink } from '@spotify/confidence-core-react';
import {
  MetricsV1MetricInput,
  MetricsV1MetricMetricTypeSpecInput,
} from '@spotify/confidence-graphql';

import {
  EntitySelect,
  FactTableSelect,
  WarehouseSelectorMui as WarehouseSelector,
} from '../../components';
import { useAdvancedWarehouseMode, useDataWarehouses } from '../../hooks';
import { defaultSpecs } from '../../metricTypes';

type MetricInputsFormProps = {
  value: MetricsV1MetricInput;
  onChange: (newInput: MetricsV1MetricInput) => void;
  defaultSpec?: MetricsV1MetricMetricTypeSpecInput;
};

export const MetricInputsForm = ({
  value,
  onChange,
  defaultSpec,
}: MetricInputsFormProps) => {
  const [isAdvancedMode] = useAdvancedWarehouseMode();
  const { primaryWarehouse } = useDataWarehouses();
  const [selectedWarehouse, setSelectedWarehouse] = useState<string>();

  const resolvedWarehouse = selectedWarehouse ?? primaryWarehouse?.name;
  const factTableWarehouse = isAdvancedMode ? resolvedWarehouse : undefined;

  // Reset previously selected columns if fact table or entity changes
  const getDefaultTypeSpec = (): MetricsV1MetricMetricTypeSpecInput => {
    if (defaultSpec) {
      return defaultSpec;
    }
    if (value.typeSpec?.averageMetricSpec) {
      return defaultSpecs.average;
    } else if (value.typeSpec?.ratioMetricSpec) {
      return defaultSpecs.ratio;
    }
    return {};
  };

  const handleWarehouseChange = (warehouseName: string) => {
    setSelectedWarehouse(warehouseName);
    onChange({
      ...value,
      factTable: '',
      filter: null,
      typeSpec: getDefaultTypeSpec(),
    });
  };

  return (
    <>
      {isAdvancedMode && (
        <WarehouseSelector
          value={selectedWarehouse}
          onChange={handleWarehouseChange}
         data-track-source-anchor="4b9c99ff54;metric-inputs-form-warehouse-selector"/>
      )}
      <EntitySelect
        label="Entity"
        value={value.entity ?? undefined}
        onChange={entity =>
          onChange({
            ...value,
            entity,
            factTable: '',
            typeSpec: getDefaultTypeSpec(),
          })
        }
        helperText={
          <>
            Entities represent what you experiment on, for example users.{' '}
            <ConfidenceWebsiteLink route="/docs/entities" underline="always" data-track-source-anchor="ec5e73d932">
              Learn more
            </ConfidenceWebsiteLink>
          </>
        }
       data-track-source-anchor="0d345b2060"/>
      <FactTableSelect
        value={value.factTable ?? undefined}
        onChange={factTable => {
          if (factTable !== value.factTable) {
            onChange({
              ...value,
              factTable,
              filter: null,
              typeSpec: getDefaultTypeSpec(),
            });
          }
        }}
        helperText={
          <>
            Fact tables contain events or measurements that describe entities.{' '}
            <ConfidenceWebsiteLink route="/docs/fact-tables" underline="always" data-track-source-anchor="36fe9e173a">
              Learn more
            </ConfidenceWebsiteLink>
          </>
        }
        disabled={!value.entity}
        entities={value.entity ? [value.entity] : []}
        warehouse={factTableWarehouse}
       data-track-source-anchor="8db79ed688;metric-inputs-form-fact-table-select"/>
    </>
  );
};
